/**
 * Shared logic for the connect-style apply/join consent dialogs
 * (ConnectApplyDialog.web / ConnectApplySheet and the join twins). Platform
 * files render; everything decision-shaped lives here:
 *   - main-CV resolution (the SAME path ApplyJobSheet uses),
 *   - consent versions + idempotency keys + replay detection.
 *
 * Screening questions and typed answers live in `screening-answers.ts`: this
 * module pulls in the CV hooks, and the apply surfaces (plus their tests) need
 * the question logic without them.
 */
import { useMemo } from 'react';
import type { JobApplicationSummary } from '@tabbio/types';
import { useTailoredCVs } from '../../hooks/useTailoredCvApi';
import {
  buildCvListWithMainFirst,
  isMasterCvListItem,
  type CvListItem,
} from '../../lib/cv-main-default';

export const CONNECT_APPLY_CONSENT_VERSION = 'connect-apply-v1';
export const CONNECT_JOIN_CONSENT_VERSION = 'connect-join-v1';

/**
 * A submit that lands but whose response is lost to a flaky connection must be
 * retryable without double-applying: one key per dialog open, reused across
 * retries of that open (PRD §8.4 replays return the existing row).
 */
export function createConnectIdempotencyKey(kind: 'apply' | 'join') {
  const uuid = globalThis.crypto?.randomUUID?.();
  const suffix =
    uuid ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `connect-${kind}-${suffix}`;
}

/**
 * The apply endpoint is idempotent: an existing application comes back instead
 * of an error. An application created before this dialog opened is therefore a
 * replay ("already applied"), not a fresh submission. 5s of slack covers clock
 * skew between the request landing and the row's server timestamp.
 */
export function isReplayedApplication(
  application: Pick<JobApplicationSummary, 'appliedAt'>,
  openedAtMs: number,
) {
  const appliedAtMs = Date.parse(application.appliedAt);
  return Number.isFinite(appliedAtMs) && appliedAtMs < openedAtMs - 5_000;
}

export interface MainApplyCvState {
  isLoading: boolean;
  /** The CV the apply/join will submit (main/default first, like ApplyJobSheet). */
  mainCv: CvListItem | null;
  /** True when the main CV is the profile-backed master ("Main CV"). */
  isProfileBacked: boolean;
  /** `cvId` to send: 'master' resolves server-side to the profile main CV. */
  cvId: string | null;
}

/**
 * Resolve the CV that will be submitted the same way ApplyJobSheet does:
 * `useTailoredCVs` + `buildCvListWithMainFirst` with the default-auto-apply CV
 * (else the profile-backed master) pinned first.
 */
export function useMainApplyCv(
  enabled: boolean,
  masterTitleOf: (hasDefaultCv: boolean) => string,
): MainApplyCvState {
  const { data: cvs, isLoading } = useTailoredCVs(enabled);
  const defaultCvId = cvs?.find((cv) => cv.isDefaultAutoApplyCv)?.id ?? null;
  const mainCv = useMemo(() => {
    const sorted = buildCvListWithMainFirst(
      cvs ?? [],
      defaultCvId,
      masterTitleOf(Boolean(defaultCvId)),
    );
    return sorted[0] ?? null;
  }, [cvs, defaultCvId, masterTitleOf]);

  return {
    isLoading,
    mainCv,
    isProfileBacked: isMasterCvListItem(mainCv),
    cvId: mainCv?.id ?? null,
  };
}
